import type { FC } from 'react';
import React from 'react';
import { StaticActions as actions } from '@alfa-bank/site-form-utils/actions';
import { useNamedContainers } from '@alfa-bank/site-named-container/src/named-container/utils';
import cn from 'classnames';

import { LegalConditionsContent } from './components/conditions';
import { LegalAgreementContent } from './components/agreement';
import type { TFormAgreement, ICheckbox } from './models';

/**
 * Пользовательское соглашение
 */
export const FormAgreement: FC<React.PropsWithChildren<TFormAgreement>> = ({
    AgreementCheckbox,
    Field,
    Text,
    analytics,
    appendix,
    children,
    className,
    dataTestId = 'checkbox',
    defaultChecked,
    editionNames,
    editions,
    getCaption,
    isError,
    modalSize = 'fullwidth',
    name = 'sopdConfirmed',
    styles,
    validateAgreement,
    withoutBKI,
    withoutConditions,
}) => {
    // Подготавливаем именованные контейнеры
    const namedContainers = useNamedContainers(children);

    const workWithNamedContainers = Object.keys(namedContainers).length > 0;

    const renderEditions = () => {
        if (editions) {
            return editions.map(({ name: editionName, value }, index) => (
                <Field<boolean>
                    key={index}
                    initialValue={value}
                    name={editionName}
                    render={({ input }) => (
                        <input name={input.name} type="hidden" value={input.value.toString()} />
                    )}
                />
            ));
        } else if (editionNames) {
            return editionNames.map((fieldName, index) => (
                <Field<boolean>
                    key={index}
                    initialValue
                    name={fieldName}
                    render={({ input }) => (
                        <input name={input.name} type="hidden" value={input.value.toString()} />
                    )}
                />
            ));
        }
    };

    return (
        <>
            <Field<boolean>
                initialValue={defaultChecked}
                name={name}
                render={({ input, meta }) => {
                    const asChecked = input.value || false;
                    const value = asChecked.toString();

                    return (
                        <div className={className}>
                            {children && !workWithNamedContainers ? (
                                React.cloneElement(children as React.ReactElement, {
                                    onChange: ({ checked }: ICheckbox) =>
                                        actions.onChange(input, meta)(checked),
                                    value,
                                    name,
                                    checked: asChecked,
                                    forwardedRef: actions.getRef(meta),
                                })
                            ) : (
                                <AgreementCheckbox
                                    agreementModalContent={LegalAgreementContent}
                                    analytics={analytics}
                                    appendix={appendix}
                                    checked={asChecked}
                                    conditionsModalContent={LegalConditionsContent}
                                    dataTestId={dataTestId}
                                    forwardedRef={actions.getRef(meta)}
                                    modalSize={modalSize}
                                    name={name}
                                    namedContainers={namedContainers}
                                    onChange={({ checked }: ICheckbox) =>
                                        actions.onChange(input, meta)(checked)
                                    }
                                    value={value}
                                    withoutBKI={withoutBKI}
                                    withoutConditions={withoutConditions}
                                />
                            )}
                            {isError(meta) && (
                                <Text
                                    className={cn(styles.external, styles.captionError)}
                                    dataTestId={`${dataTestId}-error-caption`}
                                    kind="caption"
                                    view="primary-small"
                                >
                                    {getCaption(meta)}
                                </Text>
                            )}
                        </div>
                    );
                }}
                validate={validateAgreement}
            />
            {/* Fields для контроля виджетов EditionCheckbox в детях*/}
            {renderEditions()}
        </>
    );
};
